import { useEffect, useState } from "react";
import { Phone, MessageCircle, Loader2, Inbox as InboxIcon, Search, Download, FileSpreadsheet } from "lucide-react";
import { api, API } from "../../lib/api";
import { styleFor, LEAD_KIND_STYLES } from "../../lib/leadKindStyles";
import { formatPhoneDisplay, telLink, whatsappLink } from "../../lib/phone";
import LeadDetailModal from "./LeadDetailModal";

const STATUS_BADGE = {
    new: "bg-amber-100 text-amber-800",
    contacted: "bg-blue-100 text-blue-700",
    in_progress: "bg-indigo-100 text-indigo-700",
    converted: "bg-emerald-100 text-emerald-700",
    lost: "bg-slate-100 text-slate-600",
    under_review: "bg-blue-100 text-blue-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
};

const STATUS_OPTIONS = [
    { v: "all", l: "All" },
    { v: "new", l: "New" },
    { v: "contacted", l: "Contacted" },
    { v: "in_progress", l: "In progress" },
    { v: "converted", l: "Converted" },
    { v: "lost", l: "Lost" },
];

const KIND_OPTIONS = [
    { v: "", l: "All types" },
    { v: "inquiries", l: "Contact inquiries" },
    { v: "registrations", l: "Demo registrations" },
    { v: "tutor-applications", l: "Tutor applications" },
];

export default function UnifiedInbox() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(null);
    const [status, setStatus] = useState("all");
    const [kind, setKind] = useState("");
    const [search, setSearch] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportMsg, setExportMsg] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status !== "all") params.set("status", status);
            if (kind) params.set("kind", kind);
            if (search.trim()) params.set("search", search.trim());
            const r = await api.get(`/admin/inbox?${params.toString()}`);
            setItems(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    const exportXlsx = async () => {
        setExporting(true);
        setExportMsg("");
        try {
            const params = new URLSearchParams();
            if (status !== "all") params.set("status", status);
            if (kind) params.set("kind", kind);
            if (search.trim()) params.set("search", search.trim());
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            const token = localStorage.getItem("at_token");
            const res = await fetch(`${API}/admin/inbox/export.xlsx?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            const blob = await res.blob();
            // Server sets X-Export-Count header when present; fall back to items.length
            const exportedCount = parseInt(res.headers.get("X-Export-Count") || "", 10) || items.length;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const ts = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, "");
            a.download = `unified-inbox-${ts}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setExportMsg(`Downloaded ${exportedCount} lead${exportedCount === 1 ? "" : "s"}`);
            setTimeout(() => setExportMsg(""), 4000);
        } catch (e) {
            setExportMsg(e.message || "Export failed");
            setTimeout(() => setExportMsg(""), 5000);
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [status, kind, search]);

    const counters = items.reduce((acc, it) => {
        const k = it._kind;
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    return (
        <div data-testid="unified-inbox">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div>
                    <h2 className="font-heading text-2xl font-bold">Unified Inbox</h2>
                    <p className="text-sm text-slate-500 mt-1">All leads in one stream — newest first.</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${LEAD_KIND_STYLES.inquiries.dot}`}/>Inquiries: <strong className="text-[#0F172A]">{counters.inquiries || 0}</strong></span>
                    <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${LEAD_KIND_STYLES.registrations.dot}`}/>Demos: <strong className="text-[#0F172A]">{counters.registrations || 0}</strong></span>
                    <span className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${LEAD_KIND_STYLES["tutor-applications"].dot}`}/>Tutors: <strong className="text-[#0F172A]">{counters["tutor-applications"] || 0}</strong></span>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
                <div className="grid md:grid-cols-[1fr_200px_200px] gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, phone, email, city, message…"
                            data-testid="inbox-search"
                            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                    </div>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        data-testid="inbox-status"
                        className="px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    >
                        {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        data-testid="inbox-kind"
                        className="px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                    >
                        {KIND_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                    <div>
                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">From date</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            max={dateTo || undefined}
                            data-testid="inbox-export-date-from"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">To date</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            min={dateFrom || undefined}
                            data-testid="inbox-export-date-to"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={exportXlsx}
                        disabled={exporting}
                        data-testid="inbox-export-xlsx-btn"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm whitespace-nowrap"
                        title="Download current view as Excel (.xlsx) — multi-sheet workbook"
                    >
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                        {exporting ? "Preparing…" : "Export to Excel"}
                    </button>
                </div>
                {exportMsg && (
                    <div data-testid="inbox-export-msg" className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1.5">
                        <Download className="w-3.5 h-3.5" />
                        {exportMsg}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
            ) : items.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                    <InboxIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <div className="text-sm">No leads match these filters.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((it) => {
                        const ks = styleFor(it._kind);
                        return (
                        <button
                            key={`${it._kind}-${it.id}`}
                            onClick={() => setView(it)}
                            data-testid={`inbox-lead-${it.id}`}
                            data-kind={it._kind}
                            className={`w-full text-left bg-white border border-slate-200 ${ks.leftBorder} rounded-xl p-4 ${ks.cardHover} hover:shadow-sm transition-all`}
                        >
                            <div className="flex items-start gap-4">
                                <div className={`w-10 h-10 rounded-full ${ks.accentBg} ${ks.accentText} flex items-center justify-center font-heading font-bold shrink-0`}>
                                    {(it.name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center flex-wrap gap-2 mb-1">
                                        <div className="font-heading font-semibold text-[#0F172A]">{it.name || "—"}</div>
                                        <span data-testid={`inbox-kind-badge-${it._kind}`} className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${ks.badge}`}>{ks.short}</span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${STATUS_BADGE[it.status || "new"]}`}>
                                            {(it.status || "new").replace(/_/g, " ")}
                                        </span>
                                        <span className="text-xs text-slate-400 ml-auto">{new Date(it.created_at).toLocaleString()}</span>
                                    </div>
                                    <div className="text-sm text-slate-600 mb-1">
                                        {formatPhoneDisplay(it.phone)}{it.email ? ` · ${it.email}` : ""}{it.city ? ` · ${it.city}` : ""}
                                    </div>
                                    <div className="text-sm text-slate-500 line-clamp-1">{it.subject || it.subjects || it.grade || it.qualification || it.message || "—"}</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {it.phone && (
                                        <a href={telLink(it.phone)} onClick={(e) => e.stopPropagation()} aria-label="Call" className="p-2 rounded-lg bg-blue-50 text-[#1E3A8A] hover:bg-blue-100">
                                            <Phone className="w-4 h-4" />
                                        </a>
                                    )}
                                    {it.phone && (
                                        <a
                                            href={whatsappLink(it.phone)}
                                            target="_blank" rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label="WhatsApp"
                                            className="p-2 rounded-lg bg-green-50 text-[#25D366] hover:bg-green-100"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </button>
                        );
                    })}
                </div>
            )}

            {view && (
                <LeadDetailModal
                    kind={view._kind}
                    item={view}
                    onClose={() => setView(null)}
                    onSaved={load}
                    onOpened={(id, openedAt, newStatus) => {
                        // V170.5 — optimistic patch so the status badge flips
                        // from NEW → Contacted instantly in the Unified Inbox
                        // list without waiting for the next background poll.
                        setItems((prev) => prev.map((it) => (
                            it.id === id
                                ? { ...it, opened_at: openedAt, ...(newStatus ? { status: newStatus } : {}) }
                                : it
                        )));
                    }}
                />
            )}
        </div>
    );
}
